import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from './useHybridAuth';
import { useDriverStateV2 } from './useDriverStateV2';
import { DriverCall } from '../services/driverApi';
import { filterVisibleDispatches } from '../utils/dispatchFilter';
import type { CallSortBy, ListParams } from '@relay-towing/api';
import { getList } from '../api/list';

const getCompanyDispatches = async (
  token: string,
  companyId: string
): Promise<DriverCall[]> => {
  try {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const toYMD = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const endDate = toYMD(now);
    const startDateObj = new Date(now);
    startDateObj.setDate(startDateObj.getDate() - 60);
    const startDate = toYMD(startDateObj);

    const params = {
      startDate,
      endDate,
      sortBy: 'date-dsc',
      pageSize: 500,
    } satisfies ListParams<CallSortBy> & { startDate: string; endDate: string };

    // Use the /calls endpoint instead since there's no company-wide dispatches endpoint
    // This endpoint requires the x-company-id header to filter by company
    const { data } = await getList<DriverCall>('/calls', params, {
      token,
      companyId,
    });
    // Exclude ACCEPTED_AWAITING (not actionable by drivers)
    return filterVisibleDispatches(data);
  } catch (error: any) {
    console.error('Failed to get company dispatches:', error);
    throw new Error(error.message || 'Failed to get company dispatches');
  }
};

/**
 * Hook to fetch all company-wide dispatches (calls)
 * Used by the Drivers tab to show dispatch list for all drivers
 * Note: This uses the /calls endpoint with x-company-id header
 */
export const useCompanyDispatches = () => {
  const { getAccessTokenSilently } = useHybridAuth();
  const { driver } = useDriverStateV2();

  return useQuery({
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      if (!driver?.companyId) {
        throw new Error('Company ID not available');
      }
      return getCompanyDispatches(token, driver.companyId);
    },
    queryKey: ['company-dispatches', driver?.companyId],
    enabled: !!driver?.companyId,
    staleTime: 15 * 1000, // 15 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds for real-time updates
  });
};
